import { useState, FormEvent } from 'react';

export default function Hero() {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d7734hqqo7glkto3e0og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050508] text-white">
      {/* Cyberpunk Grid Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,255,180,0.06) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0,255,180,0.06) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Scan line effect */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, rgba(0,0,0,0.15) 4px)',
      }}></div>

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/6 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #00ffb4 0%, transparent 70%)' }}></div>
      <div className="absolute bottom-1/4 right-1/6 w-[400px] h-[400px] rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #00d4ff 0%, transparent 70%)' }}></div>

      {/* Corner decorations */}
      <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-[#00ffb4]/40"></div>
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-[#00ffb4]/40"></div>
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-[#00ffb4]/40"></div>
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-[#00ffb4]/40"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
        {/* System badge */}
        <div className="inline-flex items-center gap-3 border border-[#00ffb4]/30 bg-[#00ffb4]/5 px-5 py-2 rounded-full text-xs tracking-widest mb-10">
          <span className="w-2 h-2 rounded-full bg-[#00ffb4] animate-pulse"></span>
          <span className="font-mono text-[#00ffb4]">SYSTEM STATUS: ONLINE — V4.0 BETA</span>
        </div>

        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img
            src="https://static.readdy.ai/image/378dd0f6400ec53d43ae7d1d7dd92295/8acfdc25fd3b0de57798749a193ed73d.png"
            alt="TWINODE 雙節點系統 Logo"
            className="h-20 w-20 object-contain opacity-90"
          />
        </div>

        {/* H1 */}
        <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight">
          <span className="text-white">TWINODE 雙節點系統</span>
          <br />
          <span style={{ color: '#00ffb4' }}>多維時空變量解耦引擎</span>
        </h1>

        {/* H2 */}
        <p className="text-base md:text-lg text-gray-300 mb-4 max-w-3xl mx-auto leading-relaxed">
          摒棄玄學黑盒。我們透過<strong className="text-white">多模型共振演算法</strong>，將您的出廠設定與<strong className="text-white">目標年度環境變量</strong>進行碰撞，
        </p>
        <p className="text-base md:text-lg text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
          為您生成絕對理性的<strong className="text-white">【決策導航座標】</strong>。
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => setShowModal(true)}
            className="relative overflow-hidden border border-[#00ffb4] text-[#00ffb4] px-8 py-4 rounded-md text-sm font-bold hover:bg-[#00ffb4] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer group"
          >
            <span className="relative z-10">申請 V4.0 Beta 專屬邀請碼（限量 50 席）</span>
            <div className="absolute inset-0 bg-[#00ffb4] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
          </button>
          <button
            onClick={() => scrollToSection('how-it-works')}
            className="text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap cursor-pointer underline underline-offset-4"
          >
            查看系統運作邏輯 →
          </button>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="border-t border-[#00ffb4]/30 pt-4">
            <div className="text-3xl font-bold mb-2 text-white">5 分鐘</div>
            <div className="text-xs text-gray-500 font-mono">自動化交付時效</div>
          </div>
          <div className="border-t border-[#00ffb4]/30 pt-4">
            <div className="text-3xl font-bold mb-2 text-white">6 大矩陣</div>
            <div className="text-xs text-gray-500 font-mono">戰略報告核心模組</div>
          </div>
          <div className="border-t border-[#00ffb4]/30 pt-4">
            <div className="text-3xl font-bold mb-2" style={{ color: '#00ffb4' }}>確定性核心</div>
            <div className="text-xs text-gray-500 font-mono">零幻覺演算精度</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#00ffb4]/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-[#00ffb4]/50 rounded-full"></div>
        </div>
      </div>

      {/* Beta Invite Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setShowModal(false)}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div
            className="relative bg-[#0a0a0f] text-white rounded-lg max-w-lg w-full p-10 border border-[#00ffb4]/20 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#00ffb4]/50 rounded-tl-lg"></div>
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-[#00ffb4]/50 rounded-tr-lg"></div>
            <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-[#00ffb4]/50 rounded-bl-lg"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#00ffb4]/50 rounded-br-lg"></div>

            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {!submitted ? (
              <>
                <div className="mb-8">
                  <div className="inline-flex items-center gap-2 border border-[#00ffb4]/30 bg-[#00ffb4]/5 px-4 py-1.5 rounded-full text-xs tracking-widest text-[#00ffb4] mb-6">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00ffb4] animate-pulse"></span>
                    BETA ACCESS — 限量 50 席
                  </div>
                  <h3 className="text-2xl font-bold mb-4 leading-tight">
                    申請 V4.0 Beta<br />
                    <span style={{ color: '#00ffb4' }}>專屬邀請碼</span>
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    提交您的 Email，系統將在 24 小時內發送一次性授權邀請碼（OTP）。邀請碼核發後，即可進入加密終端機啟動您的多維解耦運算。
                  </p>
                </div>

                <form
                  id="beta-invite-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                >
                  <label className="text-xs tracking-widest text-gray-500 block mb-3 font-mono">
                    輸入您的 EMAIL 地址
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#00ffb4]/50 transition-colors mb-4"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full border border-[#00ffb4] text-[#00ffb4] hover:bg-[#00ffb4] hover:text-black px-6 py-3 rounded-md text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? '提交中...' : '提交申請 → 獲取邀請碼'}
                  </button>
                  <p className="text-xs text-gray-600 text-center mt-4 font-mono">
                    您的資料受 TLS 1.3 加密保護，僅用於邀請碼核發。
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto border border-[#00ffb4]/30 rounded-full mb-6">
                  <i className="ri-check-line text-3xl" style={{ color: '#00ffb4' }}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">申請已接收</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  系統已記錄您的申請。邀請碼將在 24 小時內發送至您的信箱。請保持關注。
                </p>
                <div className="mt-6 font-mono text-xs text-gray-600">
                  STATUS: QUEUED — AWAITING AUTHORIZATION
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
